import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "./Logo";
import {
  Mail,
  Phone,
  MapPin,
  Shield,
  Heart,
  ExternalLink,
  ArrowRight,
} from "lucide-react";

export const Footer: React.FC = () => {
  return (
    <footer className="bg-slate-900 text-slate-200 pt-16 pb-12 border-t border-slate-800">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-10 pb-12 border-b border-slate-700">
          {/* Brand Col */}
          <div className="lg:col-span-2 space-y-4">
            <Logo light />
            <p className="text-slate-300 text-sm leading-relaxed max-w-md pt-2">
              Feingold Property Management, LLC is one of the most engaging,
              customer-focused, innovative, and technologically savvy management
              companies in the country. Serving commercial and residential
              property owners with unyielding integrity, transparency, and
              corporate responsibility.
            </p>
            <div className="flex items-center gap-4 text-xs text-slate-300 pt-2">
              <span className="flex items-center gap-1.5">
                <Shield className="w-4 h-4 text-accent" /> Equal Housing
                Opportunity
              </span>
              <span className="flex items-center gap-1.5">
                <Heart className="w-4 h-4 text-rose-400" /> Community Dedicated
              </span>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-4 font-sans">
              Navigation
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link to="/" className="hover:text-white transition-colors">
                  Home
                </Link>
              </li>
              <li>
                <Link
                  to="/about"
                  className="hover:text-white transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/pricing"
                  className="hover:text-white transition-colors"
                >
                  Pricing & Plans
                </Link>
              </li>
              <li>
                <Link
                  to="/guarantees"
                  className="hover:text-white transition-colors"
                >
                  Service Commitments
                </Link>
              </li>
              <li>
                <Link
                  to="/testimonials"
                  className="hover:text-white transition-colors"
                >
                  Client Testimonials
                </Link>
              </li>
              <li>
                <Link to="/blog" className="hover:text-white transition-colors">
                  Market Insights Articles
                </Link>
              </li>
            </ul>
          </div>

          {/* Property Services */}
          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-4 font-sans">
              Services
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/property-management"
                  className="hover:text-white transition-colors"
                >
                  Property Management
                </Link>
              </li>
              <li>
                <Link
                  to="/marketing"
                  className="hover:text-white transition-colors"
                >
                  Marketing & Leasing
                </Link>
              </li>
              <li>
                <Link
                  to="/tenant-screening"
                  className="hover:text-white transition-colors"
                >
                  Tenant Screening
                </Link>
              </li>
              <li>
                <Link
                  to="/rent-collection"
                  className="hover:text-white transition-colors"
                >
                  Rent Collection
                </Link>
              </li>
              <li>
                <Link
                  to="/maintenance-services"
                  className="hover:text-white transition-colors"
                >
                  24/7 Maintenance
                </Link>
              </li>
              <li>
                <Link
                  to="/accounting"
                  className="hover:text-white transition-colors"
                >
                  Accounting & Reports
                </Link>
              </li>
              <li>
                <Link
                  to="/eviction"
                  className="hover:text-white transition-colors"
                >
                  Eviction Coordination
                </Link>
              </li>
            </ul>
          </div>

          {/* Calculators */}
          <div>
            <h4 className="text-white font-semibold text-sm tracking-wider uppercase mb-4 font-sans">
              Calculators & Tools
            </h4>
            <ul className="space-y-2.5 text-sm">
              <li>
                <Link
                  to="/rent-sell-calculator"
                  className="hover:text-white transition-colors"
                >
                  Rent vs Sell Calculator
                </Link>
              </li>
              <li>
                <Link
                  to="/vacancy-loss-calculator"
                  className="hover:text-white transition-colors"
                >
                  Vacancy Calculator
                </Link>
              </li>
              <li>
                <Link
                  to="/roi-calculator"
                  className="hover:text-white transition-colors"
                >
                  ROI Calculator
                </Link>
              </li>
              <li className="pt-2">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-1 text-white font-semibold hover:text-accent"
                >
                  Contact Support <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom copyright */}
        <div className="pt-8 flex flex-col md:flex-row items-center justify-between text-xs text-slate-300 gap-4">
          <div>
            © 2019 - 2026 Feingold Property Management, LLC. All rights
            reserved.
          </div>
          <div className="flex items-center gap-6">
            <Link to="/privacy-policy" className="hover:text-white">
              Privacy Policy
            </Link>
            <Link to="/terms-of-service" className="hover:text-white">
              Terms of Service
            </Link>
            <Link to="/fair-housing" className="hover:text-white">
              Fair Housing Statement
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
